import React, { useState } from 'react';
import { Droppable } from '@hello-pangea/dnd';
import { Leg, LegNode, LegNodeTemplate, Client } from '../../types';
import LegContainer from './LegContainer';

interface Props {
  legs: Leg[];
  legNodes: LegNode[];
  templates: LegNodeTemplate[];
  onAddLeg: (name: string) => void;
  onRenameLeg: (legId: string, name: string) => void;
  onToggleLegActive: (legId: string, isActive: boolean) => void;
  onDeleteLeg: (legId: string) => void;
  onCopyLegIn: (legId: string) => void;
  onCopyLegOut: (legId: string, targetDataBodyId: string) => Promise<void>;
  loadCopyTargets: () => Promise<{ id: string; name: string; status: 'active' | 'paused' | 'archived' }[]>;
  onAddNode: (legId: string, template: LegNodeTemplate, parentNodeId?: string) => void;
  onRemoveNode: (nodeId: string) => void;
  onUpdateNodeConfig: (nodeId: string, config: Record<string, any>) => void;
  /** Account client directory, so each node card can label its client. */
  clients: Client[];
  /** Called after a client is assigned inside a node's config modal. */
  onClientAssigned: () => void;
}

/**
 * Multi-leg view: each leg is its own column. Up to ~3 legs fit comfortably
 * side-by-side; beyond that the row scrolls horizontally rather than
 * crushing each column below readable width.
 *
 * The "+ Add Leg" placeholder column sits at the right of every state:
 *   - 0 legs → just the placeholder, prompting first leg creation
 *   - 1+ legs → leg columns + placeholder at the end
 */
function LegsSection({
  legs,
  legNodes,
  templates,
  onAddLeg,
  onRenameLeg,
  onToggleLegActive,
  onDeleteLeg,
  onCopyLegIn,
  onCopyLegOut,
  loadCopyTargets,
  onAddNode,
  onRemoveNode,
  onUpdateNodeConfig,
  clients,
  onClientAssigned,
}: Props) {
  const [adding, setAdding] = useState(false);
  const [draftName, setDraftName] = useState('');

  function commitAdd() {
    const trimmed = draftName.trim();
    if (trimmed) {
      onAddLeg(trimmed);
      setDraftName('');
      setAdding(false);
    } else {
      // empty submit = use auto name "Leg N"
      onAddLeg(`Leg ${legs.length + 1}`);
      setDraftName('');
      setAdding(false);
    }
  }

  const isMulti = legs.length >= 2;

  return (
    <div className={`legs-section ${isMulti ? 'multi' : ''}`}>
      <div className="legs-title">
        The Legs (Decision Tree)
        {legs.length > 0 && (
          <span style={{ fontWeight: 400, marginLeft: 8, opacity: 0.7 }}>
            · {legs.length} leg{legs.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {/* When there are zero legs, the legacy drop zone (`legs`) catches drops
          so a user dragging a template onto the section auto-creates the
          first leg via the backend's findOrCreateDefault path. After the
          first leg exists, the per-leg drop zones handle everything. */}
      {legs.length === 0 ? (
        <Droppable droppableId="legs">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="legs-empty"
              style={{
                background: snapshot.isDraggingOver ? 'rgba(99,102,241,0.06)' : undefined,
                borderColor: snapshot.isDraggingOver ? '#6366f1' : undefined,
              }}
            >
              <p style={{ fontSize: 16, fontWeight: 500, marginBottom: 6 }}>No legs configured</p>
              <p style={{ marginBottom: 14 }}>Each leg is an independent delivery route. Add one to get started, or drag a leg template here.</p>
              {adding ? (
                <div style={{ display: 'inline-flex', gap: 6 }}>
                  <input
                    className="form-input"
                    style={{ maxWidth: 220 }}
                    autoFocus
                    placeholder="Leg name (e.g. Email Vendor A)"
                    value={draftName}
                    onChange={e => setDraftName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {commitAdd();}
                      if (e.key === 'Escape') { setDraftName(''); setAdding(false); }
                    }}
                  />
                  <button className="btn btn-primary btn-sm" onClick={commitAdd}>Add</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setDraftName(''); setAdding(false); }}>Cancel</button>
                </div>
              ) : (
                <button className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>+ Add a leg</button>
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      ) : (
        <div className="legs-row">
          {legs.map(leg => (
            <LegContainer
              key={leg.id}
              leg={leg}
              nodes={legNodes.filter(n => n.legId === leg.id)}
              templates={templates}
              onAddNode={(template, parentNodeId) => onAddNode(leg.id, template, parentNodeId)}
              onRemoveNode={onRemoveNode}
              onUpdateNodeConfig={onUpdateNodeConfig}
              clients={clients}
              onClientAssigned={onClientAssigned}
              onRename={(name) => onRenameLeg(leg.id, name)}
              onToggleActive={() => onToggleLegActive(leg.id, !leg.isActive)}
              onDelete={() => onDeleteLeg(leg.id)}
              onCopyIn={() => onCopyLegIn(leg.id)}
              onCopyOut={(targetDataBodyId) => onCopyLegOut(leg.id, targetDataBodyId)}
              loadCopyTargets={loadCopyTargets}
            />
          ))}

          {/* Add-leg placeholder column. Click adds a leg with auto name
              "Leg N", or with a custom name if the user types one in. */}
          <div className="leg-add-column">
            {adding ? (
              <div style={{ padding: 16, width: '100%' }}>
                <input
                  className="form-input"
                  autoFocus
                  placeholder={`Leg ${legs.length + 1}`}
                  value={draftName}
                  onChange={e => setDraftName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {commitAdd();}
                    if (e.key === 'Escape') { setDraftName(''); setAdding(false); }
                  }}
                />
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button className="btn btn-primary btn-sm" style={{ flex: 1, justifyContent: 'center' }} onClick={commitAdd}>Add</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setDraftName(''); setAdding(false); }}>Cancel</button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setAdding(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'inherit',
                  font: 'inherit',
                  padding: 24,
                  width: '100%',
                  height: '100%',
                }}
              >
                <div style={{ fontSize: 28, lineHeight: 1, marginBottom: 6 }}>+</div>
                <div style={{ fontSize: 13 }}>Add a leg</div>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default LegsSection;
